import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  ExportAssortmentSearchCriteria,
  ExportStockByLocationCriteria,
  ExportStockByProductCriteria,
  ExportUserReportCriteria,
  ShipmentExportCriteria
} from '@shared/models';
import { DeliveryOrderExportPickingCriteria } from '@shared/models/delivery-order.model';
import { GoodsReceiveExportCriteria } from '@shared/models/goods-receive.model';
import { OrderExportCriteria } from '@shared/models/order.model';
import { PurchaseRequestExportCriteria } from '@shared/models/purchase-request.model';
import { ReceiveOrderExportCriteria } from '@shared/models/receive-order.model';
import {
  AgentPaymentTransactionReportCriteria,
  AwaitingFirstLotExportCriteria,
  BillPaymentTransactionReportCriteria,
  CJXMemberRewardTransactionCriteria,
  CJXSalesSummaryReportCriteria,
  CafeBillOfMaterialsCostReportCriteria,
  CafeOrderProcessingPerformanceReportCriteria,
  CafeSalesAndProfitReportCriteria,
  CheckProductTagCriteria,
  CollectExpiredPromotionTagCriteria,
  CountItemInToteReportCriteria,
  CustomerSatisfactionSurveyReportCriteria,
  DestroyTransactionReportCriteria,
  DispatchToteReportCriteria,
  EdcPaymentTransactionReportCriteria,
  GiftWithPurchaseReportCriteria,
  InventoryAdjustmentRequestReportExportCriteria,
  LinemanOrderMissingReceiptReportCriteria,
  LinemanProductCriteria,
  LinemanSalesReconcileTransactionReportCriteria,
  LoadingGroupByWarehouseCriteria,
  MemberProfileReportCriteria,
  MobilePhoneSaleAndRefundReportCriteria,
  MoneyInOutReportCriteria,
  MovingAverageReportExportCriteria,
  PointActivityReportCriteria,
  PointBurnTransactionReportCriteria,
  ProductEcommerceReportCriteria,
  ProductReportExportCriteria,
  ProductRetailPriceConfidentialReportCriteria,
  QRPaymentTransactionReportCriteria,
  RecallCreditNoteReportCriteria,
  RecallStockCheckConfirmationReportCriteria,
  RecallStockPackingConfirmationReportCriteria,
  ReceiptCouponTransactionReportCriteria,
  ReceiveToteReportCriteria,
  ReduceToClearSummaryReportCriteria,
  ReduceToClearTransactionReportCriteria,
  RefundExceptionTransactionReportCriteria,
  RefundExportCriteria,
  ReturnToSupplierReportCriteria,
  ReturnToteReportCriteria,
  SalePerformanceReportCriteria,
  SalesSummaryReportExportCriteria,
  SalesTaxReportCriteria,
  ShelfFixAssetReportCriteria,
  ShelfInventoryReportCriteria,
  ShelfReplenishmentPhotoReviewCriteria,
  ShelfReplenishmentSummaryReportCriteria,
  ShelfReplenishmentTransactionReportCriteria,
  ShiftRecordReportCriteria,
  StockCardExportCriteria,
  StockCountAssignmentSummaryReportCriteria,
  StockCountAssignmentTransactionReportCriteria,
  StockCountByQISummaryReportCriteria,
  StockCountByQITransactionReportCriteria,
  StockCountByStaffTransactionReportCriteria,
  StockRefillPerformanceCriteria,
  StockRefillSummaryCriteria,
  StoreAvailableAdjustmentRequestReportCriteria,
  StoreExpenseReportCriteria,
  StoreGroupExportCriteria,
  StoreStockRecallReportCriteria,
  SupplierCouponTransactionExportCriteria,
  SupplierPartnerCouponCriteria
} from '@shared/models/report.model';
import { RewardCatalogExportCriteria } from '@shared/models/reward-catalog.model';
import { SupplierExportCriteria } from '@shared/models/supplier.model';
import { BaseService } from '@shared/services/base.service';

import { ExportAdjustCriteria } from '../../manage-stock/models/adjust-stock.model';
import { RewardExportCriteria } from '../../reward/models/reward.model';
import { StoreConsultantExportCriteria } from '../../store-consultant/models/store-consultant.model';

@Injectable()
export class NewReportService extends BaseService {
  constructor(private readonly http: HttpClient) {
    super();
    this.envService = this.env.services.exportReport;
    this.headers = new HttpHeaders(this.envService.headers);
  }

  exportAwaitingFirstLot(criteria: AwaitingFirstLotExportCriteria): Observable<any> {
    return this.exportReport(this.envService.awaitingFirstLot, criteria);
  }

  exportOrder(criteria: OrderExportCriteria): Observable<any> {
    return this.exportReport(this.envService.order, criteria);
  }

  exportMovingAverage(criteria: MovingAverageReportExportCriteria): Observable<any> {
    return this.exportReport(this.envService.movingAverage, criteria);
  }

  exportReceiveOrderReport(criteria: ReceiveOrderExportCriteria): Observable<any> {
    return this.exportReport(this.envService.receiveOrder, criteria);
  }

  exportShelfInventory(criteria: ShelfInventoryReportCriteria): Observable<any> {
    return this.exportReport(this.envService.shelfInventory, criteria);
  }

  exportShipment(criteria: ShipmentExportCriteria): Observable<any> {
    return this.exportReport(this.envService.shipment, criteria);
  }

  exportGoodsReceive(criteria: GoodsReceiveExportCriteria): Observable<any> {
    return this.exportReport(this.envService.goodsReceive, criteria);
  }

  exportPointBurnTransaction(criteria: PointBurnTransactionReportCriteria): Observable<any> {
    return this.exportReport(this.envService.pointBurnTransaction, criteria);
  }

  exportDeliveryOrder(reportCriteria: DeliveryOrderExportPickingCriteria) {
    return this.exportReport(this.envService.deliveryOrder, reportCriteria);
  }

  exportRewardCatalog(payload: RewardCatalogExportCriteria) {
    return this.exportReport(this.envService.rewardCatalog, payload);
  }

  exportShelf(payload: ShelfFixAssetReportCriteria) {
    return this.exportReport(this.envService.shelfFixAsset, payload);
  }

  exportUserReport(payload: ExportUserReportCriteria) {
    return this.exportReport(this.envService.users, payload);
  }

  exportStoreConsultant(payload: StoreConsultantExportCriteria) {
    return this.exportReport(this.envService.storeConsultant, payload);
  }

  exportPr(criteria: PurchaseRequestExportCriteria): Observable<any> {
    return this.exportReport(this.envService.exportPr, criteria);
  }

  exportSupplier(criteria: SupplierExportCriteria): Observable<any> {
    return this.exportReport(this.envService.exportSupplier, criteria);
  }

  exportInventoryAdjustmentRequest(criteria: InventoryAdjustmentRequestReportExportCriteria): Observable<any> {
    return this.exportReport(this.envService.exportInventoryAdjustmentRequest, criteria);
  }

  exportReward(criteria: RewardExportCriteria): Observable<any> {
    return this.exportReport(this.envService.reward, criteria);
  }

  exportDestroyTransaction(criteria: DestroyTransactionReportCriteria): Observable<any> {
    return this.exportReport(this.envService.destroyTransactionExport, criteria);
  }

  public stockByProductExport(criteria: ExportStockByProductCriteria): Observable<any> {
    return this.exportReport(this.envService.stockByProductExport, criteria);
  }

  public stockByLocationExport(criteria: ExportStockByLocationCriteria): Observable<any> {
    return this.exportReport(this.envService.stockByLocationExport, criteria);
  }

  public stockCardExport(criteria: StockCardExportCriteria): Observable<any> {
    return this.exportReport(this.envService.stockCardExport, criteria);
  }

  public salesSummaryReportExport(criteria: SalesSummaryReportExportCriteria) {
    return this.exportReport(this.envService.salesSummaryReport, criteria);
  }

  exportReduceToClearSummary(criteria: ReduceToClearSummaryReportCriteria): Observable<any> {
    return this.exportReport(this.envService.reduceToClearSummary, criteria);
  }

  public exportReduceToClearTransaction(criteria: ReduceToClearTransactionReportCriteria) {
    return this.exportReport(this.envService.reduceToClearTransaction, criteria);
  }

  exportCompanyAssortment(criteria: ExportAssortmentSearchCriteria): Observable<any> {
    return this.exportReport(this.envService.exportCompanyAssortment, criteria);
  }

  exportCompanyAssortmentConfidential(criteria: ExportAssortmentSearchCriteria): Observable<any> {
    return this.exportReport(this.envService.exportAssortmentConfidential, criteria);
  }

  public exportReceiptCouponTransaction(criteria: ReceiptCouponTransactionReportCriteria) {
    return this.exportReport(this.envService.receiptCoupon, criteria);
  }

  public exportStockCountByQISummary(criteria: StockCountByQISummaryReportCriteria) {
    return this.exportReport(this.envService.stockCountByQISummary, criteria);
  }

  public exportStockCountByQITransaction(criteria: StockCountByQITransactionReportCriteria) {
    return this.exportReport(this.envService.stockCountByQITransaction, criteria);
  }

  public exportStoreAvailableAdjustmentRequest(criteria: StoreAvailableAdjustmentRequestReportCriteria) {
    return this.exportReport(this.envService.storeAvailableAdjustmentRequest, criteria);
  }

  public exportStockCountAssignmentSummary(criteria: StockCountAssignmentSummaryReportCriteria) {
    return this.exportReport(this.envService.stockCountAssignmentSummary, criteria);
  }

  public exportStockCountAssignmentTransaction(criteria: StockCountAssignmentTransactionReportCriteria) {
    return this.exportReport(this.envService.stockCountAssignmentTransaction, criteria);
  }

  public exportStockCountByStaffTransaction(criteria: StockCountByStaffTransactionReportCriteria) {
    return this.exportReport(this.envService.stockCountByStaffTransaction, criteria);
  }

  public exportStoreExpense(criteria: StoreExpenseReportCriteria) {
    return this.exportReport(this.envService.storeExpense, criteria);
  }

  public exportStoreOtherIncome(criteria: StoreExpenseReportCriteria) {
    return this.exportReport(this.envService.storeOtherIncome, criteria);
  }

  exportRefundSummary(criteria: RefundExportCriteria) {
    return this.exportReport(this.envService.refundSummary, criteria);
  }

  exportSupplierCouponIssueTransaction(criteria: SupplierCouponTransactionExportCriteria) {
    return this.exportReport(this.envService.supplierCouponIssueTransaction, criteria);
  }

  exportSupplierCouponTransaction(criteria: SupplierCouponTransactionExportCriteria) {
    return this.exportReport(this.envService.supplierCouponTransaction, criteria);
  }

  exportRefundTransaction(criteria: RefundExportCriteria) {
    return this.exportReport(this.envService.refundTransaction, criteria);
  }

  exportSalePerformance(criteria: SalePerformanceReportCriteria) {
    return this.exportReport(this.envService.salePerformance, criteria);
  }

  exportCountItemInTote(criteria: CountItemInToteReportCriteria) {
    return this.exportReport(this.envService.countItemInTote, criteria);
  }

  exportShiftRecord(criteria: ShiftRecordReportCriteria) {
    return this.exportReport(this.envService.shiftRecord, criteria);
  }

  exportMoneyInOut(criteria: MoneyInOutReportCriteria) {
    return this.exportReport(this.envService.moneyInOut, criteria);
  }

  exportCJXSalesSummary(criteria: CJXSalesSummaryReportCriteria) {
    return this.exportReport(this.envService.cjxSalesSummary, criteria);
  }

  exportStockAdjustment(criteria: ExportAdjustCriteria) {
    return this.exportReport(this.envService.stockAdjustment, criteria);
  }

  exportCafeBillOfMaterialsCost(criteria: CafeBillOfMaterialsCostReportCriteria) {
    return this.exportReport(this.envService.cafeBillOfMaterialsCost, criteria);
  }

  exportCafeSalesAndProfit(criteria: CafeSalesAndProfitReportCriteria) {
    return this.exportReport(this.envService.cafeSalesAndProfit, criteria);
  }

  exportSalesTax(criteria: SalesTaxReportCriteria) {
    return this.exportReport(this.envService.salesTax, criteria);
  }

  exportMemberProfileReport(criteria: MemberProfileReportCriteria) {
    return this.exportReport(this.envService.memberProfile, criteria);
  }

  exportPointActivityReport(criteria: PointActivityReportCriteria) {
    return this.exportReport(this.envService.pointActivity, criteria);
  }

  exportCJXShelfInventory(criteria: ShelfInventoryReportCriteria): Observable<any> {
    return this.exportReport(this.envService.cjxShelfInventory, criteria);
  }

  exportCJXMemberRewardTransactions(criteria: CJXMemberRewardTransactionCriteria): Observable<any> {
    return this.exportReport(this.envService.cjxMemberRewardTransaction, criteria);
  }

  exportGiftWithPurchase(criteria: GiftWithPurchaseReportCriteria): Observable<any> {
    return this.exportReport(this.envService.giftWithPurchase, criteria);
  }

  exportStoreStockRecall(criteria: StoreStockRecallReportCriteria): Observable<any> {
    return this.exportReport(this.envService.storeStockRecall, criteria);
  }

  exportRecallStockPackingConfirmation(criteria: RecallStockPackingConfirmationReportCriteria): Observable<any> {
    return this.exportReport(this.envService.recallStockPackingConfirmation, criteria);
  }

  exportStockRefillSummary(criteria: StockRefillSummaryCriteria) {
    return this.exportReport(this.envService.stockRefillSummary, criteria);
  }

  exportStockRefillPerformance(criteria: StockRefillPerformanceCriteria) {
    return this.exportReport(this.envService.stockRefillPerformance, criteria);
  }

  exportCafeOrderProcessingPerformance(criteria: CafeOrderProcessingPerformanceReportCriteria) {
    return this.exportReport(this.envService.cafeOrderProcessingPerformance, criteria);
  }

  exportRecallStockCheckConfirmation(criteria: RecallStockCheckConfirmationReportCriteria) {
    return this.exportReport(this.envService.recallStockCheckConfirmation, criteria);
  }

  exportRecallCreditNote(criteria: RecallCreditNoteReportCriteria) {
    return this.exportReport(this.envService.recallCreditNote, criteria);
  }

  exportLoadingGroupByWarehouse(criteria: LoadingGroupByWarehouseCriteria) {
    return this.exportReport(this.envService.loadingGroupByWarehouse, criteria);
  }

  exportQRPaymentTransaction(criteria: QRPaymentTransactionReportCriteria) {
    return this.exportReport(this.envService.qrPaymentTransaction, criteria);
  }

  exportDispatchTote(criteria: DispatchToteReportCriteria): Observable<any> {
    return this.exportReport(this.envService.dispatchTote, criteria);
  }

  exportReceiveTote(criteria: ReceiveToteReportCriteria): Observable<any> {
    return this.exportReport(this.envService.receiveTote, criteria);
  }

  exportReturnTote(criteria: ReturnToteReportCriteria): Observable<any> {
    return this.exportReport(this.envService.returnTote, criteria);
  }

  exportShelfReplenishmentSummary(criteria: ShelfReplenishmentSummaryReportCriteria) {
    return this.exportReport(this.envService.shelfReplenishmentSummary, criteria);
  }

  exportShelfReplenishmentTransaction(criteria: ShelfReplenishmentTransactionReportCriteria) {
    return this.exportReport(this.envService.shelfReplenishmentTransaction, criteria);
  }

  exportShelfReplenishmentPhotoReview(criteria: ShelfReplenishmentPhotoReviewCriteria): Observable<any> {
    return this.exportReport(this.envService.shelfReplenishmentPhotoReview, criteria);
  }

  exportCheckProductTag(criteria: CheckProductTagCriteria): Observable<any> {
    return this.exportReport(this.envService.checkProductTag, criteria);
  }

  exportCollectExpiredPromotionTag(criteria: CollectExpiredPromotionTagCriteria): Observable<any> {
    return this.exportReport(this.envService.collectExpiredPromotionTag, criteria);
  }

  exportStoreGroup(criteria: StoreGroupExportCriteria): Observable<any> {
    return this.exportReport(this.envService.storeGroup, criteria);
  }

  exportBillPaymentTransaction(criteria: BillPaymentTransactionReportCriteria) {
    return this.exportReport(this.envService.billPaymentTransaction, criteria);
  }

  exportSupplierPartnerCoupon(criteria: SupplierPartnerCouponCriteria): Observable<any> {
    return this.exportReport(this.envService.supplierPartnerCoupon, criteria);
  }

  exportEdcPaymentTransaction(criteria: EdcPaymentTransactionReportCriteria): Observable<any> {
    return this.exportReport(this.envService.edcPaymentTransaction, criteria);
  }

  exportLinemanProduct(criteria: LinemanProductCriteria): Observable<any> {
    return this.exportReport(this.envService.linemanProduct, criteria);
  }

  exportLinemanSalesReconcileTransaction(criteria: LinemanSalesReconcileTransactionReportCriteria) {
    return this.exportReport(this.envService.linemanSalesReconcileTransaction, criteria);
  }

  exportLinemanOrderMissingReceipt(criteria: LinemanOrderMissingReceiptReportCriteria) {
    return this.exportReport(this.envService.linemanOrderMissingReceipt, criteria);
  }

  exportAgentPaymentTransactionReport(criteria: AgentPaymentTransactionReportCriteria): Observable<any> {
    return this.exportReport(this.envService.agentPaymentTransactionReport, criteria);
  }

  exportMobilePhoneSaleAndRefund(criteria: MobilePhoneSaleAndRefundReportCriteria) {
    return this.exportReport(this.envService.mobilePhoneSaleAndRefund, criteria);
  }

  exportRefundExceptionTransaction(criteria: RefundExceptionTransactionReportCriteria) {
    return this.exportReport(this.envService.refundExceptionTransaction, criteria);
  }

  exportReturnToSupplier(criteria: ReturnToSupplierReportCriteria) {
    return this.exportReport(this.envService.returnToSupplier, criteria);
  }

  exportCustomerSatisfactionSurvey(criteria: CustomerSatisfactionSurveyReportCriteria) {
    return this.exportReport(this.envService.customerSatisfactionSurvey, criteria);
  }

  exportProduct(criteria: ProductReportExportCriteria): Observable<any> {
    return this.exportReport(this.envService.productExport, criteria);
  }

  exportProductConfidential(criteria: ProductReportExportCriteria): Observable<any> {
    return this.exportReport(this.envService.productConfidentialExport, criteria);
  }

  exportProductRetailPriceConfidential(criteria: ProductRetailPriceConfidentialReportCriteria): Observable<any> {
    return this.exportReport(this.envService.productRetailPriceConfidentialExport, criteria);
  }

  exportProductEcommerce(criteria: ProductEcommerceReportCriteria): Observable<any> {
    return this.exportReport(this.envService.productEcommerce, criteria);
  }

  exportReport<T>(path: string, exportCriteria: T) {
    const url = this.getFullUrl(path, null, true);
    return this.http.post<any>(url, exportCriteria, {
      headers: this.loaderHeaders(0),
      observe: 'body'
    });
  }
}
